"use client";

const steps = [
  {
    phase: "PHASE 01",
    title: "Let\u2019s Talk",
    description:
      "A straightforward conversation about what you\u2019re dealing with. No sales pitch, no obligation. We\u2019ll ask questions, listen, and give you an honest assessment of whether we can help and how.",
    duration: "30\u201360 min",
    deliverable: "Honest Assessment",
  },
  {
    phase: "PHASE 02",
    title: "Scope & Proposal",
    description:
      "Based on our conversation, JAMO puts together a clear proposal\u2014what we\u2019ll do, how long it will take, and what it costs. Retainer, project-based, or fractional\u2014whatever fits your situation.",
    duration: "A few days",
    deliverable: "Clear Proposal",
  },
  {
    phase: "PHASE 03",
    title: "Get to Work",
    description:
      "JAMO works directly with your team, not around them. You get senior leadership\u2014not a junior associate. Whether that means leading your engineering org or redesigning your cloud environment, we\u2019re hands-on from day one.",
    duration: "Ongoing",
    deliverable: "Regular Updates",
  },
  {
    phase: "PHASE 04",
    title: "Measure & Adjust",
    description:
      "We define success metrics upfront and track them. If something isn\u2019t working, we adjust. The goal is real results, not billable hours.",
    duration: "Continuous",
    deliverable: "Measurable Outcomes",
  },
];

export default function ProcessSection() {
  return (
    <section id="process" aria-label="Our process" className="py-32 bg-[#0C0C0E] relative">
      {/* Vertical gold line accent */}
      <div aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-[rgba(184,152,106,0.2)] to-transparent hidden lg:block" />
      <div className="max-w-8xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-16 items-end mb-24">
          <div>
            <div className="flex items-center gap-4 mb-6 reveal-hidden" data-reveal>
              <div className="w-8 h-[1px] bg-[#B8986A]" />
              <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#B8986A]">
                How It Works
              </span>
            </div>
            <h2
              className="font-fraunces text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.05] tracking-tight text-[#F2EFE9] reveal-hidden"
              data-reveal
            >
              Structured for
              <br />
              <span className="italic text-gradient-gold">Your Outcomes.</span>
            </h2>
          </div>
          <p className="text-[#A09D97] text-lg font-light leading-relaxed reveal-hidden delay-200" data-reveal>
            No mystery, no surprises. Here&apos;s how working together actually works.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-0">
          {steps?.map((step, i) => (
            <div
              key={step?.phase}
              className={`process-step grid md:grid-cols-[80px_1fr_200px] gap-8 items-start py-12 border-b border-[rgba(242,239,233,0.06)] last:border-b-0 reveal-hidden delay-${i * 100 + 100}`}
              data-reveal
            >
              {/* Phase Number */}
              <div className="hidden md:flex items-center justify-center w-12 h-12 border border-[rgba(184,152,106,0.3)] rounded-sm">
                <span className="text-[10px] font-bold tracking-[0.15em] text-[#B8986A]">
                  {String(i + 1)?.padStart(2, "0")}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="text-[9px] font-bold tracking-[0.3em] uppercase text-[#B8986A] mb-3">
                  {step?.phase}
                </div>
                <h3 className="font-fraunces text-2xl font-light text-[#F2EFE9] mb-4 tracking-tight">
                  {step?.title}
                </h3>
                <p className="text-[#6B6864] text-sm font-light leading-relaxed max-w-xl">
                  {step?.description}
                </p>
              </div>

              {/* Meta */}
              <div className="flex md:flex-col gap-6 md:gap-4">
                <div>
                  <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#3A3A40] mb-1">
                    Duration
                  </div>
                  <div className="text-[#A09D97] text-sm font-medium">
                    {step?.duration}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] font-bold tracking-[0.2em] uppercase text-[#3A3A40] mb-1">
                    Deliverable
                  </div>
                  <div className="text-[#A09D97] text-sm font-medium">
                    {step?.deliverable}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}